import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import FeedbackColloquioModal from './FeedbackColloquioModal';
import { 
  Calendar, 
  CheckCircle, 
  Clock,
  MessageSquare,
  MessageCircle,
  Send, 
  Star,
  User,
  Users,
  Phone,
  Mail,
  MapPin,
  Edit3
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestisciMatch = ({ agencyId }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [expandedMatchId, setExpandedMatchId] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [showColloquioFeedbackModal, setShowColloquioFeedbackModal] = useState(false);
  const [selectedColloquioId, setSelectedColloquioId] = useState(null);
  const [feedbackSubmittedMatches, setFeedbackSubmittedMatches] = useState(new Set());
  const [feedback, setFeedback] = useState({
    rating: 5,
    comment: '',
    interested_in_continuing: true
  });
  const [message, setMessage] = useState({
    subject: 'Proposta Colloquio',
    text: '',
    type: 'proposal'
  });

  // Stato per pills filtro
  const [filterZone, setFilterZone] = useState(false);
  const [filterPercent, setFilterPercent] = useState(false);

  // Ricava la zona di interesse dell'agenzia
  const [agencyZone, setAgencyZone] = useState("");
  // Stato per oggetto agenzia
  const [agencyData, setAgencyData] = useState(null);
  useEffect(() => {
    const fetchAgencyZone = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        const response = await axios.get(`${API}/v1/agenzie/${agencyId}`, { headers });
        if (response.data) {
          // agencyZone ora prende zone_di_interesse (non solo zona)
          setAgencyZone(response.data.zone_di_interesse || response.data.zona);
          setAgencyData(response.data);
        }
      } catch (err) {
        setAgencyZone("");
        setAgencyData(null);
      }
    };
    if (agencyId) fetchAgencyZone();
  }, [agencyId]);

  // Filtra i match in base alle pills
  const filteredMatches = matches.filter((match) => {
    let ok = true;
    // Filtra per zona: match tra candidato.zona_preferenza_lavorativa e agencyZone
    if (filterZone && agencyZone) {
      // Gestione agencyZone come stringa o array
      if (Array.isArray(agencyZone)) {
        ok = ok && agencyZone.some(z => match.candidato?.zona_preferenza_lavorativa?.toLowerCase() === z.toLowerCase());
      } else {
        ok = ok && match.candidato?.zona_preferenza_lavorativa?.toLowerCase() === agencyZone.toLowerCase();
      }
    }
    if (filterPercent) {
      ok = ok && (match.percentuale_match || 0) >= 50;
    }
    return ok;
  });

  console.log('🎯 Filtered matches:', filteredMatches.length, '/', matches.length, '(filterZone:', filterZone, 'filterPercent:', filterPercent, ')');

  useEffect(() => {
    fetchMatches();
  }, [agencyId]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      const headers = token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {};
      
      // Converti agencyId in numero per il confronto
      const numericAgencyId = typeof agencyId === 'string' && !isNaN(agencyId) ? parseInt(agencyId) : agencyId;
      
      console.log('🔍 Fetching matches for agency ID:', numericAgencyId, 'with token:', token ? 'present' : 'missing');
      
      // Chiama API con parametro agency_id per filtrare lato backend
      const response = await axios.get(`${API}/v1/matches?agency_id=${numericAgencyId}`, { headers });
      
      console.log('✅ Matches ricevuti:', response.data.length, 'matches');
      console.log('📋 Dettagli matches:', response.data);
      setMatches(response.data);
    } catch (error) {
      console.error('❌ Error fetching matches:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Errore nel caricamento dei match');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'in_attesa_contatto': '🕒 In Attesa Contatto',
      'chiamato': '📞 Chiamato',
      'in_programmazione': '📅 In Programmazione',
      'colloquio_fissato': '✅ Colloquio Fissato',
      'colloquio_effettuato': '✔️ Colloquio Effettuato',
      'in_attesa_feedback': '⏳ In Attesa Feedback',
      'inserito': '🎉 Inserito',
      'non_interessato': '❌ Non Interessato',
      'chiuso': '🔒 Chiuso'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'in_attesa_contatto': 'bg-gray-100 text-gray-700',
      'chiamato': 'bg-blue-100 text-blue-700',
      'in_programmazione': 'bg-yellow-100 text-yellow-700',
      'colloquio_fissato': 'bg-green-100 text-green-700',
      'colloquio_effettuato': 'bg-purple-100 text-purple-700',
      'in_attesa_feedback': 'bg-orange-100 text-orange-700',
      'inserito': 'bg-emerald-100 text-emerald-700',
      'non_interessato': 'bg-red-100 text-red-700',
      'chiuso': 'bg-gray-200 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const handleScheduleInterview = async (match) => {
    setSelectedMatch(match);
    setShowScheduleModal(true);
  };

  const submitScheduleInterview = async () => {
    if (!interviewDate) {
      toast.error('Seleziona una data per il colloquio');
      return;
    }

    try {
      const token = localStorage.getItem('impersonated_token') || localStorage.getItem('token');
      const headers = token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };

      const resp = await axios.post(
        `${API}/v1/matches/${selectedMatch.id}/schedule-interview`,
        { interview_date: new Date(interviewDate).toISOString() },
        { headers }
      );

      const emailSent = resp?.data?.email_sent === true;
      const msg = resp?.data?.message || (emailSent
        ? 'Colloquio fissato! Email inviata al candidato'
        : 'Colloquio fissato (email non inviata)');
      emailSent ? toast.success(msg) : toast.warning(msg);
      setShowScheduleModal(false);
      setInterviewDate('');
      fetchMatches();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Errore nella programmazione del colloquio');
    }
  };

  const handleMarkCompleted = async (match) => {
    try {
      await axios.post(`${API}/v1/matches/${match.id}/mark-interview-completed`, {
        completed: true
      });
      
      toast.success('Colloquio segnato come effettuato!');
      fetchMatches();
    } catch (error) {
      console.error('Error marking completed:', error);
      toast.error('Errore nell\'aggiornamento stato');
    }
  };

  const handleLeaveFeedback = (match) => {
    setSelectedMatch(match);
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!feedback.rating) {
      toast.error('Seleziona una valutazione');
      return;
    }

    try {
      await axios.post(`${API}/v1/matches/${selectedMatch.id}/submit-feedback`, {
        rating: feedback.rating,
        comment: feedback.comment,
        interested_in_continuing: feedback.interested_in_continuing,
        role: 'agency'
      });
      
      toast.success('Feedback inviato con successo!');
      setShowFeedbackModal(false);
      setFeedback({ rating: 5, comment: '', interested_in_continuing: true });
      fetchMatches();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Errore nell\'invio del feedback');
    }
  };


  const handleSendMessage = async () => {
    if (!message.text.trim()) {
      toast.error('Il messaggio non può essere vuoto');
      return;
    }

    try {
      let richiestaId = null;
      
      const token = localStorage.getItem('token');
      const headers = token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };
      
      try {
        // Prima prova a cercare una richiesta esistente
        const searchResponse = await axios.get(
          `${API}/v1/richieste-colloquio/search/by-parties?candidato_id=${selectedMatch.candidate_id}&agenzia_id=${agencyId}`,
          { headers }
        );
        richiestaId = searchResponse.data.id;
        console.log('Richiesta esistente trovata:', richiestaId);
      } catch (searchError) {
        // Se non esiste (404), creala
        if (searchError.response?.status === 404) {
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + 7);
          const dateString = futureDate.toISOString().split('T')[0];
          
          const richiestaResponse = await axios.post(`${API}/v1/richieste-colloquio`, {
            candidato_id: selectedMatch.candidate_id,
            agenzia_id: agencyId,
            messaggio: `Richiesta per discussione proposta`,
            data_colloquio: dateString,
            ora_colloquio: '10:00:00'
          }, { headers });
          
          richiestaId = richiestaResponse.data.id;
          console.log('Nuova richiesta creata:', richiestaId);
        } else {
          throw searchError;
        }
      }
      
      // Ora invia il messaggio con il richiesta_id
      await axios.post(`${API}/messages`, {
        richiesta_id: richiestaId,
        sender_type: 'agenzia',
        sender_id: agencyId,
        message: message.text
      }, { headers });
      
      toast.success('💬 Messaggio inviato con successo!');
      setShowMessageModal(false);
      setMessage({ subject: 'Proposta Colloquio', text: '', type: 'proposal' });
      setSelectedMatch(null);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nell\'invio del messaggio';
      toast.error(errorMessage);
    }
  };

  const handleOpenColloquioFeedback = async (match) => {
    try {
      // Salva il match selezionato
      setSelectedMatch(match);
      
      // Cerca la richiesta colloquio associata al match
      const token = localStorage.getItem('token');
      const headers = token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {};
      
      const response = await axios.get(`${API}/v1/richieste-colloquio/search/by-parties?candidato_id=${match.candidate_id}&agenzia_id=${agencyId}`, { headers });
      
      if (response.data) {
        setSelectedColloquioId(response.data.id);
        setShowColloquioFeedbackModal(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Nessun colloquio trovato per questo match. Programma prima un colloquio.');
      } else {
        console.error('Errore nel recupero colloquio:', error);
        toast.error('Errore nel caricamento del colloquio');
      }
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
        <Users className="w-6 h-6 text-blue-600" />
        Gestisci Match - Colloqui
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Pills di filtro */}

        {filteredMatches.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nessun match disponibile</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Candidato</th>
                  <th className="text-left p-3 font-semibold">Data Colloquio</th>
                  <th className="text-left p-3 font-semibold">Stato</th>
                  <th className="text-left p-3 font-semibold">Feedback</th>
                  <th className="text-left p-3 font-semibold">Messaggi</th>
                  <th className="text-left p-3 font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatches.map((match) => (
                  <React.Fragment key={match.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <User className="w-8 h-8 text-blue-500" />
                          <div>
                            <p className="font-semibold">{match.candidate_name}</p>
                            <div className="flex gap-2 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {match.candidate_phone || match.telefono}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {match.candidate_email}
                              </span>
                            </div>
                            {match.candidato?.zona_preferenza_lavorativa && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {match.candidato.zona_preferenza_lavorativa}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        {match.interview_date ? (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-green-600" />
                            <span className="text-sm">
                              {new Date(match.interview_date).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Non programmato</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(match.status)}`}>
                          {getStatusLabel(match.status)}
                        </span>
                      </td>
                      <td className="p-3">
                        {match.feedback_agenzia || match.feedback_candidato ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenColloquioFeedback(match)}
                            className="text-xs"
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Vedi Feedback
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">Nessun feedback</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedMatch(match);
                            setShowMessageModal(true);
                          }}
                          className="text-xs"
                        >
                          <MessageCircle className="w-3 h-3 mr-1" />
                          Invia
                        </Button>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {match.status === 'in_attesa_contatto' && (
                            <Button
                              size="sm"
                              onClick={() => handleScheduleInterview(match)}
                              className="bg-blue-600 hover:bg-blue-700 text-xs"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Fissa Colloquio
                            </Button>
                          )}
                          {match.status === 'colloquio_fissato' && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkCompleted(match)}
                              className="bg-green-600 hover:bg-green-700 text-xs"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completato
                            </Button>
                          )}
                          {match.status === 'colloquio_effettuato' && !match.feedback_agenzia && (
                            <Button
                              size="sm"
                              onClick={() => handleOpenColloquioFeedback(match)}
                              className="bg-purple-600 hover:bg-purple-700 text-xs"
                            >
                              <Star className="w-3 h-3 mr-1" />
                              Feedback
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Schedule Interview Modal */}
    {showScheduleModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Fissa Colloquio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Candidato</Label>
              <p className="text-sm text-gray-600">{selectedMatch?.candidate_name}</p>
            </div>
            <div>
              <Label htmlFor="interviewDate">Data e Ora Colloquio</Label>
              <Input
                id="interviewDate"
                type="datetime-local"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduleModal(false);
                  setInterviewDate('');
                }}
              >
                Annulla
              </Button>
              <Button onClick={submitScheduleInterview} className="bg-blue-600">
                Conferma
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}

    {/* Feedback Modal */}
    {showFeedbackModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Lascia Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Candidato</Label>
              <p className="text-sm text-gray-600">{selectedMatch?.candidate_name}</p>
            </div>
            
            <div>
              <Label>Valutazione (1-5 stelle)</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFeedback({ ...feedback, rating: star })}
                    className={`text-2xl ${star <= feedback.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                  >
                    ⭐
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="comment">Commento (opzionale)</Label>
              <textarea
                id="comment"
                value={feedback.comment}
                onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
                className="w-full mt-1 p-2 border rounded-md"
                rows="4"
                placeholder="Inserisci il tuo feedback..."
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="interested"
                checked={feedback.interested_in_continuing}
                onChange={(e) => setFeedback({ ...feedback, interested_in_continuing: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="interested" className="cursor-pointer">
                Interessato a proseguire la selezione
              </Label>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedback({ rating: 5, comment: '', interested_in_continuing: true });
                }}
              >
                Annulla
              </Button>
              <Button onClick={submitFeedback} className="bg-purple-600">
                Invia Feedback
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}

    {/* Modal Invia Messaggio */}
    {showMessageModal && selectedMatch && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Invia Messaggio a {selectedMatch.candidate_name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo Messaggio</label>
              <select
                value={message.type}
                onChange={(e) => setMessage({ ...message, type: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="proposal">💼 Proposta Colloquio</option>
                <option value="update">📢 Aggiornamento</option>
                <option value="general">💬 Messaggio Generico</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Oggetto</label>
              <input
                type="text"
                value={message.subject}
                onChange={(e) => setMessage({ ...message, subject: e.target.value })}
                className="w-full p-2 border rounded-lg"
                placeholder="Es: Proposta Colloquio"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Messaggio</label>
              <textarea
                value={message.text}
                onChange={(e) => setMessage({ ...message, text: e.target.value })}
                className="w-full p-2 border rounded-lg min-h-[150px]"
                placeholder={`Ciao ${selectedMatch.candidate_name?.split(' ')[0]}, ti propongo un colloquio conoscitivo. Fammi sapere la tua disponibilità!`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Il candidato riceverà una notifica e una email automatica.
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMessageModal(false);
                  setMessage({ subject: 'Proposta Colloquio', text: '', type: 'proposal' });
                  setSelectedMatch(null);
                }}
              >
                Annulla
              </Button>
              <Button 
                onClick={handleSendMessage} 
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-1" />
                Invia Messaggio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}

    {/* Modal Feedback Colloquio */}
    {showColloquioFeedbackModal && selectedColloquioId && (
      <FeedbackColloquioModal
        isOpen={showColloquioFeedbackModal}
        onClose={(success) => {
          setShowColloquioFeedbackModal(false);
          setSelectedColloquioId(null);
          // Segna questo match come avente feedback solo se il feedback è stato inviato con successo
          if (success && selectedMatch) {
            setFeedbackSubmittedMatches(prev => new Set([...prev, selectedMatch.id]));
            setSelectedMatch(null);
          }
          fetchMatches(); // Ricarica i match dopo il feedback
        }}
        colloquioId={selectedColloquioId}
        userType="agenzia"
        colloquioData={null}
      />
    )}
  </div>
)};

export default GestisciMatch;
